import {isEmpty} from 'lodash-es';
import {Fragment} from 'react';

import {
  AmountInput,
  Button,
  CheckBox,
  Input,
  Select,
  SpinningLoader,
} from '@/components';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import type {
  AfriexAdminAmount,
  AfriexReferralTier,
  CommissionType,
} from '@/types';
import {AfriexCurrencyCode} from '@/types';

import {TierActionType} from './types';
import useTierUpdate from './useTierUpdate';

interface TierFormModalProps extends Pick<
  UserModalProps,
  'isOpen' | 'onClose'
> {
  data?: AfriexReferralTier;
  isMerchant?: boolean;
}

const TierFormModal = ({
  isOpen,
  onClose,
  data,
  isMerchant = false,
}: TierFormModalProps) => {
  const {dispatch, isLoading, handleSubmit, tier} = useTierUpdate(onClose, {
    ...data,
    type: isMerchant ? 'merchant-aggregator' : data?.type || 'referral',
  });

  const {
    name,
    isDeactivated,
    shouldAutomatePayout,
    shouldSkipReferrerPayout,
    shouldSkipReferreePayout,
    referralCode,
    cumulativeThreshold,
    bonusAmountPerReferree,
    bonusAmountPerReferrer,
    transactionWindow,
    type,
    commissionType,
  } = tier;

  const updateAction = {
    type: TierActionType.UPDATE_TIER,
    payload: {},
  };
  const updateLabel = isEmpty(data)
    ? `Create New ${isMerchant ? 'Merchant' : 'Referral'} Tier +`
    : `Update ${isMerchant ? 'Merchant' : 'Referral'} Tier`;
  const submitLabel = isEmpty(data) ? 'Submit' : 'Update';
  const isDisabled = isEmpty(tier);

  const commissionTypes = [
    {key: 'Fixed', value: 'fixed'},
    {key: 'Percentage', value: 'percentage'},
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-96"
      height={
        shouldSkipReferrerPayout && shouldSkipReferreePayout
          ? 'h-auto'
          : 'h-4/5'
      }
      closeOnOutsideClick
      title={updateLabel}
    >
      <div className="px-4">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // Fire-and-forget: useTierUpdate's handleSubmit try/catches,
              // clears `isLoading` and reports through showErrorMessage /
              // showSuccessMessage, so this only stops an unexpected throw
              // becoming an unhandled rejection.
              handleSubmit(tier).catch(() => {});
            }}
            className="flex flex-col"
          >
            <div className="mt-5 mb-3">
              <Input
                name="name"
                label="Name"
                value={name ?? ''}
                className="w-full"
                onChange={(e) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      name: e.target?.value ?? name,
                    },
                  })
                }
              />
            </div>

            {(isMerchant || type === 'merchant-aggregator') && (
              <>
                <div className="mt-5 mb-3">
                  <Select
                    label="Commission Type"
                    value={commissionType}
                    options={commissionTypes}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      dispatch({
                        ...updateAction,
                        payload: {
                          commissionType: e.target.value as CommissionType,
                        },
                      })
                    }
                  />
                </div>
              </>
            )}

            <CheckBox
              label="Mark to skip referrer payout"
              type="checkbox"
              checked={Boolean(shouldSkipReferrerPayout)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch({
                  ...updateAction,
                  payload: {
                    shouldSkipReferrerPayout: Boolean(e.target?.checked),
                  },
                })
              }
            />

            <CheckBox
              label="Mark to skip referee payout"
              type="checkbox"
              checked={Boolean(shouldSkipReferreePayout)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch({
                  ...updateAction,
                  payload: {
                    shouldSkipReferreePayout: Boolean(e.target?.checked),
                  },
                })
              }
            />

            {!shouldSkipReferrerPayout && (
              <div className="mt-5">
                <AmountInput
                  label={`${isMerchant ? 'Commission' : 'Bonus'} Amount For Referrer (USD)`}
                  fixedCurrency={AfriexCurrencyCode.USD}
                  value={{
                    amount: bonusAmountPerReferrer,
                    currency: AfriexCurrencyCode.USD,
                  }}
                  onChange={(value: AfriexAdminAmount) =>
                    dispatch({
                      ...updateAction,
                      payload: {
                        bonusAmountPerReferrer: value?.amount
                          ? Number(value.amount)
                          : bonusAmountPerReferrer,
                      },
                    })
                  }
                />
              </div>
            )}

            {!shouldSkipReferreePayout && (
              <div className="mt-5">
                <AmountInput
                  label="Bonus Amount Per Referee (USD)"
                  fixedCurrency={AfriexCurrencyCode.USD}
                  value={{
                    amount: bonusAmountPerReferree,
                    currency: AfriexCurrencyCode.USD,
                  }}
                  onChange={(value: AfriexAdminAmount) =>
                    dispatch({
                      ...updateAction,
                      payload: {
                        bonusAmountPerReferree: value?.amount
                          ? Number(value.amount)
                          : bonusAmountPerReferree,
                      },
                    })
                  }
                />
              </div>
            )}

            {(!shouldSkipReferrerPayout || !shouldSkipReferreePayout) && (
              <Fragment>
                <div className="mt-5">
                  <AmountInput
                    fixedCurrency={AfriexCurrencyCode.USD}
                    value={{
                      amount: cumulativeThreshold,
                      currency: AfriexCurrencyCode.USD,
                    }}
                    label="Cumulative Transaction Threshold (USD)"
                    onChange={(value: AfriexAdminAmount) =>
                      dispatch({
                        ...updateAction,
                        payload: {
                          cumulativeThreshold: value?.amount
                            ? Number(value.amount)
                            : cumulativeThreshold,
                        },
                      })
                    }
                  />
                </div>
                <div className="mt-5">
                  <Input
                    name="transactionWindow"
                    label="Transaction Window (days)"
                    type="number"
                    value={transactionWindow}
                    className="w-full"
                    onChange={(e) =>
                      dispatch({
                        ...updateAction,
                        payload: {
                          transactionWindow: Number(e.target?.value),
                        },
                      })
                    }
                  />
                </div>
                <div className="mt-5">
                  <CheckBox
                    label="Mark to automate payout"
                    type="checkbox"
                    checked={shouldAutomatePayout}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      dispatch({
                        ...updateAction,
                        payload: {
                          shouldAutomatePayout: Boolean(e.target?.checked),
                        },
                      })
                    }
                  />
                </div>
              </Fragment>
            )}

            <div className="mt-5">
              <Input
                name="referralCode"
                label="Referral Code (optional)"
                value={referralCode || ''}
                className="w-full"
                onChange={(e) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      referralCode: e.target?.value?.toLowerCase() || '',
                    },
                  })
                }
              />
            </div>

            <div className="mt-5">
              <CheckBox
                label="Mark as active"
                type="checkbox"
                checked={!isDeactivated}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    ...updateAction,
                    payload: {
                      isDeactivated: !e.target?.checked,
                    },
                  })
                }
              />
            </div>

            <div className="flex w-full justify-end space-x-2 py-5">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                colorScheme="cyan"
              >
                Cancel
              </Button>

              <Button
                disabled={isDisabled}
                type="submit"
                variant="solid"
                colorScheme="cyan"
              >
                {submitLabel}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default TierFormModal;
